import type { Access } from './access'

/**
 * A1, the token build only (the iPad app, auth-build.ts): the three
 * credentials the API issues, kept by the app because the API's cookies
 * cannot reach it. The web app never runs this: its browser keeps httpOnly
 * cookies that no script, the app's included, can read.
 *
 * - Each credential goes only where it is needed, and only to the API's own
 *   origin: the device credential to the lock screen's calls and the
 *   customer's signing screen; the signing grant to that screen alone; the
 *   session to everything else.
 * - What the app holds follows the screen (`followAccess`), never an error
 *   code: a lock drops the session, the turn to the customer drops it too, a
 *   revoked device drops everything, the stored credential included.
 * - Nothing here logs, and nothing reaches React: the answers that issue a
 *   credential lose it before the screens read them.
 */
export type CredentialKind = 'device' | 'session' | 'signing'

/** The app's every call says so (the API: http/credentials.ts). */
export const AUTH_MODE_HEADER = 'x-auth-mode'
export const CREDENTIAL_HEADERS = {
  device: 'x-staff-device',
  session: 'x-staff-session',
  signing: 'x-staff-signing',
} as const satisfies Record<CredentialKind, string>

/** What the API issues: 32 random bytes, base64url. Nothing else is ever kept or sent. */
const TOKEN = /^[A-Za-z0-9_-]{43}$/

export function isToken(value: unknown): value is string {
  return typeof value === 'string' && TOKEN.test(value)
}

/**
 * What the device store held at start. `unavailable` is not "not enrolled":
 * the store could not answer (the Keychain refuses reads on an iPad not yet
 * unlocked since it started), so the app says so and tries again, rather than
 * sending an enrolled device to enroll a second time.
 */
export type StoredDevice =
  | { state: 'absent' }
  | { state: 'present'; credential: string }
  | { state: 'unavailable' }

/**
 * Where the device credential outlives the app being closed: the Keychain in
 * the iPad app (A2), the page's localStorage in a browser build of the app.
 */
export interface DeviceStore {
  load(): Promise<StoredDevice>
  save(credential: string): Promise<void>
  clear(): Promise<void>
}

const DEVICE_KEY = 'rental.staff.device'

/** A browser build's store: the app's tests in a browser (A1c), never the iPad app. */
export function browserDeviceStore(storage: () => Storage): DeviceStore {
  return {
    async load() {
      let value: string | null
      try {
        value = storage().getItem(DEVICE_KEY)
      } catch {
        return { state: 'unavailable' }
      }
      // Not a credential the API issued: as good as none, and enrolling replaces it.
      return isToken(value) ? { state: 'present', credential: value } : { state: 'absent' }
    },
    async save(credential) {
      storage().setItem(DEVICE_KEY, credential)
    },
    async clear() {
      storage().removeItem(DEVICE_KEY)
    },
  }
}

/** The iPad app until its shell supplies the Keychain (A2): it never falls back to the page. */
export const UNAVAILABLE_STORE: DeviceStore = {
  load: async () => ({ state: 'unavailable' }),
  save: async () => {
    throw new Error('no device store')
  },
  clear: async () => {},
}

export interface Held {
  device: string | null
  session: string | null
  signing: string | null
  /** A session the screen locked on: sent to /lock only, until the server has answered. */
  closingSession: string | null
  /** A grant the customer's screen ended on: sent to /signing/end only, until answered. */
  closingSigning: string | null
}

export const NOTHING_HELD: Held = {
  device: null,
  session: null,
  signing: null,
  closingSession: null,
  closingSigning: null,
}

/**
 * What the app still holds once the screen moves from `before` to `next`, and
 * whether the stored device credential goes too. The new session or grant
 * itself was kept from the answer that issued it, just before.
 */
export function followAccess(
  held: Held,
  before: Access,
  next: Access,
): { held: Held; wipe: boolean } {
  switch (next.state) {
    case 'unlocked':
      // The server ends every other session on the device when it opens one.
      return {
        held: { ...held, signing: null, closingSession: null, closingSigning: null },
        wipe: false,
      }
    case 'locked':
      if (before.state === 'signing') {
        return {
          held: { ...held, signing: null, closingSigning: held.signing ?? held.closingSigning },
          wipe: false,
        }
      }
      return {
        held: { ...held, session: null, closingSession: held.session ?? held.closingSession },
        wipe: false,
      }
    case 'signing':
      // The turn ended the session on the server: there is nothing left to end.
      return { held: { ...held, session: null, closingSession: null }, wipe: false }
    case 'enroll':
      if (next.reason === 'revoked') return { held: NOTHING_HELD, wipe: true }
      // Not enrolled: never wiped. The server says so when no credential reached
      // it too; the next enrollment replaces whatever is stored.
      return {
        held: { ...held, session: null, signing: null, closingSession: null, closingSigning: null },
        wipe: false,
      }
    case 'checking':
    case 'update':
      return { held, wipe: false }
  }
}

/** Which credentials a call to the API carries, by its path. */
type Route = 'public' | 'enroll' | 'device' | 'signing' | 'signingEnd' | 'lock' | 'staff'

const STAFF_PREFIX = '/v1/staff/'

function routeOf(pathname: string): Route {
  if (!pathname.startsWith(STAFF_PREFIX)) return 'public'
  switch (pathname.slice(STAFF_PREFIX.length)) {
    case 'devices/enroll':
      return 'enroll'
    case 'device':
    case 'sign-in':
    case 'unlock':
      return 'device'
    case 'signing':
    case 'signing/sign':
      return 'signing'
    case 'signing/end':
      return 'signingEnd'
    case 'lock':
      return 'lock'
    default:
      return 'staff'
  }
}

function carried(route: Route, held: Held): Partial<Record<CredentialKind, string | null>> {
  switch (route) {
    case 'public':
    case 'enroll':
      return {}
    case 'device':
      return { device: held.device }
    case 'signing':
      return { device: held.device, signing: held.signing }
    case 'signingEnd':
      return { device: held.device, signing: held.closingSigning ?? held.signing }
    case 'lock':
      return { session: held.closingSession ?? held.session }
    case 'staff':
      return { session: held.session }
  }
}

/** The answers that issue credentials, and which: anything else in any other answer is ignored. */
const TURN = /^\/v1\/staff\/bookings\/[^/]+\/contract\/turn$/

function issuedBy(method: string, pathname: string): readonly CredentialKind[] {
  if (method !== 'POST') return []
  if (pathname === '/v1/staff/devices/enroll') return ['device', 'session']
  if (pathname === '/v1/staff/sign-in' || pathname === '/v1/staff/unlock') return ['session']
  if (TURN.test(pathname)) return ['signing']
  return []
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/** The same answer with another body: for the screens, the body without its credentials. */
function rebuilt(response: Response, body: string): Response {
  const headers = new Headers(response.headers)
  headers.delete('content-length')
  headers.delete('content-encoding')
  return new Response(body, { status: response.status, statusText: response.statusText, headers })
}

export interface TokenKeeper {
  /** At start: the stored device credential, kept in memory; `unavailable` means try again. */
  loadDevice(): Promise<StoredDevice['state']>
  /** A2: the iPad app's Keychain, set before the first start. */
  setStore(store: DeviceStore): void
  /** Moves the credentials along with the screen; called by every transition. */
  follow(before: Access, next: Access): void
  /** True while a session locked on screen still waits for the server to end it. */
  closingSession(): boolean
  /** The app's fetch for every call to the API: openapi-fetch, uploads, files. */
  fetch(request: Request): Promise<Response>
}

export function createTokenKeeper(options: {
  apiOrigin: string
  store: DeviceStore
  fetch?: (request: Request) => Promise<Response>
}): TokenKeeper {
  const { apiOrigin } = options
  let store = options.store
  let held: Held = NOTHING_HELD
  const send = options.fetch ?? ((request: Request) => fetch(request))

  async function keepIssued(response: Response, kinds: readonly CredentialKind[]) {
    const text = await response.text()
    let body: unknown
    try {
      body = JSON.parse(text)
    } catch {
      return rebuilt(response, text)
    }
    if (!isRecord(body) || !('credentials' in body)) return rebuilt(response, text)
    const { credentials, ...rest } = body
    if (isRecord(credentials)) {
      for (const kind of kinds) {
        const value = credentials[kind]
        if (!isToken(value)) continue
        held = { ...held, [kind]: value }
        if (kind === 'device') {
          try {
            await store.save(value)
          } catch {
            // Kept in memory for this run; the next start finds none and enrolls again.
          }
        }
      }
    }
    return rebuilt(response, JSON.stringify(rest))
  }

  return {
    async loadDevice() {
      let stored: StoredDevice
      try {
        stored = await store.load()
      } catch {
        stored = { state: 'unavailable' }
      }
      // An enrollment this run saved nothing: memory still has it, and keeps it.
      if (stored.state === 'present') held = { ...held, device: stored.credential }
      return stored.state
    },

    setStore(next) {
      store = next
    },

    follow(before, next) {
      const moved = followAccess(held, before, next)
      held = moved.held
      if (moved.wipe) {
        // A credential left behind is refused as revoked at the next start, and wiped then.
        store.clear().catch(() => {})
      }
    },

    closingSession() {
      return held.closingSession !== null
    },

    async fetch(request) {
      const url = new URL(request.url)
      // Not the API (a storage provider's signed link): nothing of the app's goes with it.
      if (url.origin !== apiOrigin) return send(request)
      const route = routeOf(url.pathname)
      const values = carried(route, held)
      const headers = new Headers(request.headers)
      for (const name of Object.values(CREDENTIAL_HEADERS)) headers.delete(name)
      headers.set(AUTH_MODE_HEADER, 'token')
      for (const kind of Object.keys(values) as CredentialKind[]) {
        const value = values[kind]
        if (value) headers.set(CREDENTIAL_HEADERS[kind], value)
      }
      // No cookies either way, and no redirect that could carry the headers elsewhere.
      const response = await send(
        new Request(request, { headers, credentials: 'omit', redirect: 'error' }),
      )
      // Answered: the session or grant has ended, or had already. A busy or failing
      // server may not have ended it, so it waits for the next try.
      if (response.status < 500 && response.status !== 429) {
        if (route === 'lock' && held.closingSession === values.session) {
          held = { ...held, closingSession: null }
        }
        if (route === 'signingEnd' && held.closingSigning === values.signing) {
          held = { ...held, closingSigning: null }
        }
      }
      const kinds = issuedBy(request.method, url.pathname)
      if (kinds.length === 0 || !response.ok || response.status === 204) return response
      return keepIssued(response, kinds)
    },
  }
}
